import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import * as Icons from '@common/components/Icons';
import LoadingIndicator from '@common/components/LoadingIndicator';
import {
  FILE_VALIDATION_INCOMPLETE_UPLOAD,
  FILE_VALIDATION_TYPE_ERROR,
} from '@common/components/JobApplyForm/JobApplyForm.helpers';

import {
  AddFileButton,
  Container,
  DeleteButton,
  FileInput,
  ValidItem,
  FilesList,
  StatusColumn,
  ValidIcon,
  InValidItem,
  InvalidItems,
  InvalidFileNameText,
  ValidFileName,
  InvalidFileName,
  RemoveInvalidButton,
  StatusIcon,
} from './FilesUpload.styled';

const errorTypeToTranslationKey = {
  [FILE_VALIDATION_INCOMPLETE_UPLOAD]: 'filesUpload.failed.incompleteUpload',
  [FILE_VALIDATION_TYPE_ERROR]: 'filesUpload.failed.invaildFileType',
};

const filterFiles = files =>
  files.reduce(
    (acc, file) => ({
      validFiles: file.isValid ? [...acc.validFiles, file] : acc.validFiles,
      invalidFiles: file.isValid
        ? acc.invalidFiles
        : [...acc.invalidFiles, file],
    }),
    { validFiles: [], invalidFiles: [] },
  );

const FilesUpload = ({
  className,
  files,
  onFilesAdded,
  onFileDelete,
  addFileButtonText,
  inputId,
}) => {
  const { validFiles, invalidFiles } = filterFiles(files);
  const { t } = useTranslation();

  return (
    <Container className={className}>
      <FilesList $hasFiles={files.length > 0}>
        {validFiles.map(file => (
          <ValidItem key={file.key} data-testid="attachedFile">
            <ValidFileName $loading={file.isLoading}>{file.name}</ValidFileName>
            <StatusIcon>
              {file.isLoading ? (
                <LoadingIndicator />
              ) : (
                <ValidIcon data-testid="validFile">
                  <Icons.Check />
                </ValidIcon>
              )}
            </StatusIcon>
            <StatusColumn>
              <DeleteButton
                data-testid="removeFile"
                type="button"
                onClick={() => onFileDelete(file)}
                icon={<Icons.Delete />}
              />
            </StatusColumn>
          </ValidItem>
        ))}
        {invalidFiles.length > 0 && (
          <InvalidItems>
            {invalidFiles.map(file => (
              <InValidItem key={file.key} data-testid="attachedFile">
                <InvalidFileName>
                  <InvalidFileNameText>{file.name}</InvalidFileNameText>{' '}
                  {file.errors
                    .map(error => t(errorTypeToTranslationKey[error]))
                    .join(', ')}
                </InvalidFileName>
                <StatusColumn>
                  <RemoveInvalidButton
                    data-testid="removeFile"
                    type="button"
                    onClick={() => onFileDelete(file)}
                    icon={<Icons.Cross />}
                  />
                </StatusColumn>
              </InValidItem>
            ))}
          </InvalidItems>
        )}
      </FilesList>

      <FileInput
        type="file"
        id={inputId}
        onChange={onFilesAdded}
        multiple
        accept="application/pdf"
      />
      <AddFileButton htmlFor={inputId}>{addFileButtonText}</AddFileButton>
    </Container>
  );
};

FilesUpload.propTypes = {
  addFileButtonText: PropTypes.string.isRequired,
  className: PropTypes.string,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      isValid: PropTypes.bool,
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  inputId: PropTypes.string.isRequired,
  onFileDelete: PropTypes.func.isRequired,
  onFilesAdded: PropTypes.func.isRequired,
};

FilesUpload.defaultProps = {
  className: undefined,
  files: [],
};

export default FilesUpload;
